import { DEFAULT_ASPECT_RATIO } from '../constants';
import { JUSTIFIED_GALLERY_BREAKPOINTS } from './constants';
import {
  ItemRenderData,
  ItemStyleProps,
  JustifiedGalleryItem,
  JustifiedGalleryItemGeneric,
  JustifiedGalleryRow,
  ResponsiveHeights,
} from './types';

/**
 * Finds which row an item belongs to based on its index.
 * @param itemIndex - The index of the item
 * @param rows - The rows of the gallery
 * @returns The row index and item count in the row
 */
export function findRowForItem<T = JustifiedGalleryItem>(
  itemIndex: number,
  rows: JustifiedGalleryRow<T>[]
): { rowIndex: number; isLastItemInRow: boolean } {
  let currentRowIndex = 0;
  let totalItemsSoFar = 0;

  for (const row of rows) {
    if (itemIndex < totalItemsSoFar + row.items.length) {
      break;
    }
    totalItemsSoFar += row.items.length;
    currentRowIndex++;
  }

  return {
    rowIndex: currentRowIndex,
    isLastItemInRow:
      itemIndex === totalItemsSoFar + rows[currentRowIndex].items.length - 1,
  };
}

/**
 * Calculates the safe aspect ratio for an item.
 */
export function getSafeAspectRatio<T = JustifiedGalleryItem>(
  item: JustifiedGalleryItemGeneric<T>
): number {
  return item.aspectRatio || DEFAULT_ASPECT_RATIO;
}
/**
 * Calculates the actual row gap when portal containers are enabled
 */
export function calculateActualRowGap(
  rowGapPx: number,
  shouldRenderPortalContainer: boolean
): number {
  return shouldRenderPortalContainer ? rowGapPx / 2 : rowGapPx;
}

/**
 * gets item render data with all necessary information
 */
export function getItemRenderData<T = JustifiedGalleryItem>(
  item: JustifiedGalleryItemGeneric<T>,
  index: number,
  rows: JustifiedGalleryRow<T>[],
  lastRowIndex: number,
  shouldLastRowStretch: boolean
): ItemRenderData<T> {
  const { rowIndex, isLastItemInRow } = findRowForItem(index, rows);
  const aspectRatio = getSafeAspectRatio(item);
  const isLastRow = rowIndex === lastRowIndex;
  const shouldStretch = !isLastRow || shouldLastRowStretch;

  return {
    item,
    index,
    rowIndex,
    isLastRow,
    shouldStretch,
    isLastItemInRow,
    aspectRatio,
  };
}

/**
 * gets style properties for an item
 */
export function getItemStyleProps<T = JustifiedGalleryItem>(
  renderData: ItemRenderData<T>
): ItemStyleProps {
  const { aspectRatio, shouldStretch } = renderData;

  return {
    flexGrow: shouldStretch ? aspectRatio : 0,
    aspectRatio: aspectRatio.toString(),
  };
}

/**
 * gets CSS gap string for the container
 */
export function getGapStyle(rowGapPx: number, columnGapPx: number): string {
  return `${rowGapPx}px ${columnGapPx}px`;
}

/**
 * Determines the current stretch range based on container width
 */
export function getCurrentHeight(
  responsiveHeights: ResponsiveHeights,
  containerWidth: number
): number {
  if (
    containerWidth >= JUSTIFIED_GALLERY_BREAKPOINTS.xl &&
    responsiveHeights.xl
  ) {
    return responsiveHeights.xl;
  }
  if (
    containerWidth >= JUSTIFIED_GALLERY_BREAKPOINTS.lg &&
    responsiveHeights.lg
  ) {
    return responsiveHeights.lg;
  }
  if (
    containerWidth >= JUSTIFIED_GALLERY_BREAKPOINTS.md &&
    responsiveHeights.md
  ) {
    return responsiveHeights.md;
  }
  if (
    containerWidth >= JUSTIFIED_GALLERY_BREAKPOINTS.sm &&
    responsiveHeights.sm
  ) {
    return responsiveHeights.sm;
  }

  // Fallback to the first available range or default
  const ranges = [
    responsiveHeights.xl,
    responsiveHeights.sm,
    responsiveHeights.lg,
    responsiveHeights.md,
  ];
  const firstAvailable = ranges.find((range) => range !== undefined);

  return firstAvailable || 245; // Default fallback
}

export const toFixed = (value: number, precision: number = 2): number => {
  return parseFloat(value.toFixed(precision));
};

export const getTargetAspectSumForRow = (
  rowItemLength: number,
  targetRowHeight: number,
  columnGapPx: number,
  containerWidth: number
): number => {
  if (!targetRowHeight) return 0;

  // For n items, there are (n-1) gaps between them
  const totalGapSpace = Math.max(0, rowItemLength - 1) * columnGapPx;
  const availableWidth = containerWidth - totalGapSpace;
  return toFixed(availableWidth / targetRowHeight, 2);
};
